import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { format, subDays } from 'date-fns';
import { 
  CalendarIcon,
  Plus,
  Fuel,
  TrendingUp,
  Package,
  Loader2,
  Edit,
  Save,
  DollarSign,
  Eye,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import { getFuelGradeColor } from '../utils/fuelGradeColors';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2
  }).format(value || 0);
};

const formatPennies = (value, decimals = 1) => {
  return `${(value || 0).toFixed(decimals)}p`;
};

export const FuelManagementPage = () => {
  const { user, isAdmin, isSuperUser } = useAuth();
  const isManager = user?.user_type === 'manager';
  const isStaff = user?.user_type === 'staff';
  const { stores } = useStore();
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [storeDetails, setStoreDetails] = useState(null);
  const [fuelStock, setFuelStock] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deliveryDialogOpen, setDeliveryDialogOpen] = useState(false);
  const [priceDialogOpen, setPriceDialogOpen] = useState(false);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [saving, setSaving] = useState(false);
  const [costPrices, setCostPrices] = useState({});
  const [costPriceDate, setCostPriceDate] = useState(new Date());
  const [savingCostPrice, setSavingCostPrice] = useState(false);
  const [editingDelivery, setEditingDelivery] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingDelivery, setDeletingDelivery] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [priceHistory, setPriceHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [viewingDelivery, setViewingDelivery] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  // Filter fuel stations based on user role - memoized to prevent infinite re-renders
  const fuelStations = useMemo(() => {
    return stores.filter(s => {
      if (s.store_type !== 'fuel_station') return false;
      // Admin/Super User/Manager can see all fuel stations
      if (isAdmin || isSuperUser || isManager) return true;
      // Standard users only see assigned stores
      return user?.assigned_stores?.includes(s.id);
    });
  }, [stores, isAdmin, isSuperUser, isManager, user?.assigned_stores]);

  // Delivery form
  const [deliveryForm, setDeliveryForm] = useState({
    delivery_date: new Date(),
    supplier: '',
    supplier_id: '',
    invoice_number: '',
    grades: []
  });

  // Price form
  const [newSellingPrice, setNewSellingPrice] = useState(0);

  useEffect(() => {
    // Auto-select first store if only one available
    if (fuelStations.length === 1 && !selectedStoreId) {
      setSelectedStoreId(fuelStations[0].id);
    }
  }, [fuelStations]);

  useEffect(() => {
    if (selectedStoreId) {
      fetchFuelStock();
      fetchDeliveries();
      fetchStoreDetails();
      fetchSuppliers(selectedStoreId);  // Load store-specific suppliers
    }
  }, [selectedStoreId]);

  const fetchStoreDetails = async () => {
    try {
      const response = await axios.get(`${API}/stores/${selectedStoreId}`);
      setStoreDetails(response.data);
      // Clear cost prices on store change - don't pre-populate
      setCostPrices({});
    } catch (error) {
      console.error('Failed to fetch store details:', error);
    }
  };

  const fetchSuppliers = async (storeId) => {
    try {
      const response = await axios.get(`${API}/suppliers`, {
        params: { store_id: storeId }
      });
      // Sort suppliers by name in ascending order (case-insensitive)
      const sortedSuppliers = (response.data || []).sort((a, b) => 
        (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
      );
      setSuppliers(sortedSuppliers);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
    }
  };

  const fetchFuelStock = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/fuel/stock/${selectedStoreId}`);
      setFuelStock(response.data || []);
    } catch (error) {
      console.error('Failed to fetch fuel stock:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeliveries = async () => {
    try {
      const response = await axios.get(`${API}/fuel/deliveries?store_id=${selectedStoreId}`);
      setDeliveries(response.data || []);
    } catch (error) {
      console.error('Failed to fetch deliveries:', error);
    }
  };

  // Fetch historical prices for selected date
  const fetchPriceHistory = async (date) => {
    if (!selectedStoreId) return;
    setLoadingHistory(true);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const response = await axios.get(`${API}/fuel/daily-prices`, {
        params: { store_id: selectedStoreId, price_date: dateStr }
      });
      const prices = response.data || [];
      // Pre-fill cost prices from history
      const priceMap = {};
      prices.forEach(p => {
        priceMap[p.grade_id] = p.cost_price?.toString() || '';
      });
      setCostPrices(priceMap);
      setPriceHistory(prices);
    } catch (error) {
      console.error('Failed to fetch price history:', error);
      setCostPrices({});
      setPriceHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Fetch last 30 days price data for chart
  const fetchChartData = async () => {
    if (!selectedStoreId) return;
    try {
      const endDate = format(new Date(), 'yyyy-MM-dd');
      const startDate = format(subDays(new Date(), 30), 'yyyy-MM-dd');
      const response = await axios.get(`${API}/fuel/daily-prices/range`, {
        params: { store_id: selectedStoreId, start_date: startDate, end_date: endDate }
      });
      
      // Transform data for chart - group by date
      const priceData = response.data || [];
      const dateMap = {};
      
      priceData.forEach(p => {
        if (!dateMap[p.date]) {
          dateMap[p.date] = { date: p.date };
        }
        dateMap[p.date][p.grade_name] = p.cost_price;
      });
      
      // Sort by date and convert to array
      const chartArray = Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));
      setChartData(chartArray);
    } catch (error) {
      console.error('Failed to fetch chart data:', error);
      setChartData([]);
    }
  };

  // Fetch chart data when store changes
  useEffect(() => {
    if (selectedStoreId) {
      fetchChartData();
    }
  }, [selectedStoreId]);

  // Fetch price history when date changes
  useEffect(() => {
    if (selectedStoreId && costPriceDate) {
      fetchPriceHistory(costPriceDate);
    }
  }, [costPriceDate, selectedStoreId]);

  const openDeliveryDialog = () => {
    // Initialize grades from current stock
    const grades = fuelStock.map(grade => ({
      grade_id: grade.id,
      grade_name: grade.name,
      quantity: 0,
      cost_per_litre: 0
    }));
    setDeliveryForm({
      delivery_date: new Date(),
      supplier: '',
      supplier_id: '',
      invoice_number: '',
      grades
    });
    setEditingDelivery(null);
    setDeliveryDialogOpen(true);
  };

  const openEditDeliveryDialog = (delivery) => {
    // Map delivery grades to form format
    const grades = fuelStock.map(grade => {
      const deliveryGrade = delivery.grades?.find(g => g.grade_id === grade.id);
      return {
        grade_id: grade.id,
        grade_name: grade.name,
        quantity: deliveryGrade?.quantity || 0,
        cost_per_litre: deliveryGrade?.cost_per_litre || 0
      };
    });
    
    setDeliveryForm({
      delivery_date: new Date(delivery.delivery_date),
      supplier: delivery.supplier || '',
      supplier_id: '',
      invoice_number: delivery.invoice_number || '',
      grades
    });
    setEditingDelivery(delivery);
    setDeliveryDialogOpen(true);
  };

  const handleSupplierSelect = (value) => {
    if (value === 'manual') {
      setDeliveryForm({ ...deliveryForm, supplier_id: '', supplier: '' });
    } else {
      const supplier = suppliers.find(s => s.id === value);
      setDeliveryForm({ 
        ...deliveryForm, 
        supplier_id: value,
        supplier: supplier?.name || ''
      });
    }
  };

  const handleDeliverySubmit = async () => {
    // Validate at least one grade has quantity
    const hasQuantity = deliveryForm.grades.some(g => g.quantity > 0);
    if (!hasQuantity) {
      toast.error('Please enter quantity for at least one fuel grade');
      return;
    }

    // Validate cost for grades with quantity
    const missingCost = deliveryForm.grades.some(g => g.quantity > 0 && !g.cost_per_litre);
    if (missingCost) {
      toast.error('Please enter cost per litre for all grades with quantity');
      return;
    }

    // Calculate totals
    const subTotal = calculateInvoiceTotal();
    const vatAmount = subTotal * 0.20;
    const totalWithVat = subTotal + vatAmount;

    setSaving(true);
    try {
      if (editingDelivery) {
        // Update existing delivery
        await axios.put(`${API}/fuel/deliveries/${editingDelivery.id}`, {
          delivery_date: format(deliveryForm.delivery_date, 'yyyy-MM-dd'),
          supplier: deliveryForm.supplier,
          invoice_number: deliveryForm.invoice_number,
          grades: deliveryForm.grades.filter(g => g.quantity > 0),
          sub_total: subTotal,
          vat_amount: vatAmount,
          total_with_vat: totalWithVat
        });
        toast.success('Fuel delivery updated successfully');
      } else {
        // Create new delivery
        await axios.post(`${API}/fuel/deliveries`, {
          store_id: selectedStoreId,
          delivery_date: format(deliveryForm.delivery_date, 'yyyy-MM-dd'),
          supplier: deliveryForm.supplier,
          invoice_number: deliveryForm.invoice_number,
          grades: deliveryForm.grades.filter(g => g.quantity > 0),
          sub_total: subTotal,
          vat_amount: vatAmount,
          total_with_vat: totalWithVat
        });
        toast.success('Fuel delivery added successfully');
      }
      
      setDeliveryDialogOpen(false);
      setEditingDelivery(null);
      fetchFuelStock();
      fetchDeliveries();
      fetchStoreDetails();
    } catch (error) {
      console.error('Failed to save delivery:', error);
      toast.error(error.response?.data?.detail || 'Failed to save delivery');
    } finally {
      setSaving(false);
    }
  };

  // Delete delivery handler
  const handleDeleteDelivery = async () => {
    if (!deletingDelivery) return;
    
    setDeleting(true);
    try {
      const response = await axios.delete(`${API}/fuel/deliveries/${deletingDelivery.id}`);
      
      const reversed = response.data?.reversed_grades || [];
      let message = 'Delivery deleted successfully!';
      if (reversed.length > 0) {
        const details = reversed.map(g => `${g.grade_name}: -${g.quantity_removed}L`).join(', ');
        message += ` Stock adjusted: ${details}`;
      }
      
      toast.success(message);
      setDeleteDialogOpen(false);
      setDeletingDelivery(null);
      
      // Refresh data - wrapped in try-catch to prevent showing error if refresh fails
      try {
        await fetchDeliveries();
        await fetchFuelStock();
      } catch (refreshError) {
        console.warn('Refresh after delete:', refreshError);
      }
    } catch (error) {
      console.error('Failed to delete delivery:', error);
      // Check if the error is actually a success (delivery was deleted but response parsing failed)
      if (error.response?.status === 200 || error.message?.includes('Network')) {
        toast.success('Delivery deleted successfully!');
        setDeleteDialogOpen(false);
        setDeletingDelivery(null);
        fetchDeliveries();
        fetchFuelStock();
      } else {
        toast.error(error.response?.data?.detail || 'Failed to delete delivery');
      }
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteDialog = (delivery) => {
    setDeletingDelivery(delivery);
    setDeleteDialogOpen(true);
  };

  const openViewDeliveryDialog = (delivery) => {
    setViewingDelivery(delivery);
    setViewDialogOpen(true);
  };

  const openPriceDialog = (grade) => {
    setSelectedGrade(grade);
    setNewSellingPrice(grade.selling_price);
    setPriceDialogOpen(true);
  };

  const handlePriceUpdate = async () => {
    if (!selectedGrade) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/stores/${selectedStoreId}/fuel-grades/${selectedGrade.id}`, {
        selling_price: parseFloat(newSellingPrice)
      });
      
      toast.success('Selling price updated');
      setPriceDialogOpen(false);
      fetchFuelStock();
    } catch (error) {
      toast.error('Failed to update price');
    } finally {
      setSaving(false);
    }
  };

  const updateGradeDelivery = (gradeId, field, value) => {
    setDeliveryForm(prev => ({
      ...prev,
      grades: prev.grades.map(g => 
        g.grade_id === gradeId ? { ...g, [field]: parseFloat(value) || 0 } : g
      )
    }));
  };

  // Calculate grade total
  const calculateGradeTotal = (grade) => {
    return (grade.quantity || 0) * (grade.cost_per_litre || 0) / 100;
  };

  // Calculate invoice total
  const calculateInvoiceTotal = () => {
    return deliveryForm.grades.reduce((sum, grade) => {
      return sum + calculateGradeTotal(grade);
    }, 0);
  };

  // Save cost prices
  const saveCostPrices = async () => {
    setSavingCostPrice(true);
    try {
      await axios.post(`${API}/fuel/cost-prices`, {
        store_id: selectedStoreId,
        date: format(costPriceDate, 'yyyy-MM-dd'),
        prices: Object.entries(costPrices).map(([gradeId, price]) => ({
          grade_id: gradeId,
          cost_price: parseFloat(price) || 0
        }))
      });
      toast.success('Cost prices saved successfully');
      fetchStoreDetails();
      fetchChartData(); // Refresh chart after saving
      fetchPriceHistory(costPriceDate); // Refresh to show saved indicator
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save cost prices');
    } finally {
      setSavingCostPrice(false);
    }
  };

  if (fuelStations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
        <Fuel className="h-12 w-12 mb-4 opacity-50" />
        <p>No fuel stations available</p>
        <p className="text-sm">
          {isAdmin || isSuperUser || isManager
            ? 'Create a fuel station in Store Management first' 
            : 'No fuel stations allocated to your account'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="fuel-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
            Fuel Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage fuel stock, deliveries, and prices
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Select value={selectedStoreId} onValueChange={setSelectedStoreId}>
            <SelectTrigger className="w-[200px]" data-testid="fuel-store-select">
              <SelectValue placeholder="Select Station" />
            </SelectTrigger>
            <SelectContent>
              {fuelStations.map(store => (
                <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedStoreId && (isAdmin || isSuperUser || isManager || isStaff) && (
            <Button onClick={openDeliveryDialog} data-testid="add-delivery-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Delivery
            </Button>
          )}
        </div>
      </div>

      {selectedStoreId && (
        <Tabs defaultValue="stock">
          <TabsList>
            <TabsTrigger value="stock">Current Stock</TabsTrigger>
            <TabsTrigger value="cost-prices">Daily Cost Prices</TabsTrigger>
            <TabsTrigger value="deliveries">Deliveries</TabsTrigger>
          </TabsList>

          {/* Current Stock Tab */}
          <TabsContent value="stock" className="mt-6">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {fuelStock.map((grade) => {
                  const gradeColors = getFuelGradeColor(grade.name);
                  return (
                  <Card key={grade.id} className="card-hover" data-testid={`fuel-grade-${grade.id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`p-3 rounded-xl ${gradeColors.bg}`}>
                            <Fuel className={`h-6 w-6 ${gradeColors.text}`} />
                          </div>
                          <div>
                            <h3 className="font-semibold">{grade.name}</h3>
                            <p className="text-sm text-muted-foreground">Stock Level</p>
                          </div>
                        </div>
                      </div>

                      <p className="text-3xl font-bold font-mono mb-4">
                        {grade.current_stock.toLocaleString()} <span className="text-lg text-muted-foreground">L</span>
                      </p>

                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Average Cost</span>
                          <span className="font-mono">{formatPennies(grade.average_price, 3)}/L</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground">Selling Price</span>
                          <div className="flex items-center gap-2">
                            <span className="font-mono">{formatPennies(grade.selling_price, 1)}/L</span>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-6 w-6"
                              onClick={() => openPriceDialog(grade)}
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex justify-between pt-2 border-t">
                          <span className="text-muted-foreground">Margin</span>
                          <span className={`font-mono font-bold ${
                            (grade.selling_price - grade.average_price) >= 0 
                              ? 'text-green-500' 
                              : 'text-red-500'
                          }`}>
                            {formatPennies(grade.selling_price - grade.average_price, 2)}/L
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Daily Cost Prices Tab */}
          <TabsContent value="cost-prices" className="mt-6">
            <div className="space-y-4">
              {/* Compact Entry Card */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-5 w-5 text-primary" />
                      <CardTitle className="text-lg" style={{ fontFamily: 'Barlow Condensed' }}>
                        Daily Cost Entry
                      </CardTitle>
                    </div>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="h-9">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {format(costPriceDate, 'dd MMM yyyy')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="end">
                        <Calendar
                          mode="single"
                          selected={costPriceDate}
                          onSelect={(date) => date && setCostPriceDate(date)}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Historical tracking only - does not affect average cost
                  </p>
                </CardHeader>
                <CardContent className="pt-0">
                  {loadingHistory ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      <span className="text-sm text-muted-foreground">Loading...</span>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Compact Table */}
                      <div className="rounded-lg border overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-muted/50">
                            <tr>
                              <th className="text-left px-3 py-2 font-medium">Grade</th>
                              <th className="text-center px-3 py-2 font-medium w-28">Avg Cost</th>
                              <th className="text-center px-3 py-2 font-medium w-32">Cost Price (p)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {fuelStock.map((grade, index) => {
                              const gradeColors = getFuelGradeColor(grade.name);
                              const hasExistingPrice = priceHistory.find(p => p.grade_id === grade.id);
                              return (
                                <tr key={grade.id} className={`border-t ${index % 2 === 0 ? 'bg-muted/20' : ''}`}>
                                  <td className="px-3 py-2">
                                    <div className="flex items-center gap-2">
                                      <div className={`p-1.5 rounded ${gradeColors.bg}`}>
                                        <Fuel className={`h-3.5 w-3.5 ${gradeColors.text}`} />
                                      </div>
                                      <span className="font-medium">{grade.name}</span>
                                      {hasExistingPrice && (
                                        <Eye className="h-3 w-3 text-green-500" title="Price recorded" />
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    <span className="font-mono text-muted-foreground">
                                      {formatPennies(grade.average_price, 3)}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    <Input
                                      type="number"
                                      step="0.001"
                                      value={costPrices[grade.id] || ''}
                                      onChange={(e) => setCostPrices(prev => ({
                                        ...prev,
                                        [grade.id]: e.target.value
                                      }))}
                                      className="w-24 mx-auto h-8 text-center font-mono text-sm"
                                      placeholder="0.000"
                                      data-testid={`cost-price-${grade.id}`}
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div className="flex justify-end">
                        <Button 
                          onClick={saveCostPrices} 
                          disabled={savingCostPrice || Object.values(costPrices).every(v => !v)} 
                          size="sm"
                          data-testid="save-cost-prices-btn"
                        >
                          {savingCostPrice ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                          Save Prices
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Price Trend Chart */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-primary" />
                    <CardTitle className="text-lg" style={{ fontFamily: 'Barlow Condensed' }}>
                      30-Day Price Trend
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  {chartData.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <TrendingUp className="h-10 w-10 mx-auto mb-3 opacity-40" />
                      <p className="text-sm">No price data recorded yet</p>
                      <p className="text-xs mt-1">Start recording daily costs to see trends</p>
                    </div>
                  ) : (
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fontSize: 11 }}
                            tickFormatter={(value) => format(new Date(value), 'dd/MM')}
                          />
                          <YAxis 
                            tick={{ fontSize: 11 }}
                            tickFormatter={(value) => `${value}p`}
                            domain={['auto', 'auto']}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'hsl(var(--card))', 
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px',
                              fontSize: '12px'
                            }}
                            labelFormatter={(value) => format(new Date(value), 'dd MMM yyyy')}
                            formatter={(value) => [`${value?.toFixed(3)}p`, '']}
                          />
                          <Legend wrapperStyle={{ fontSize: '12px' }} />
                          {fuelStock.map((grade, index) => {
                            const colors = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'];
                            return (
                              <Line
                                key={grade.id}
                                type="monotone"
                                dataKey={grade.name}
                                stroke={colors[index % colors.length]}
                                strokeWidth={2}
                                dot={{ r: 3 }}
                                activeDot={{ r: 5 }}
                                connectNulls
                              />
                            );
                          })}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Deliveries Tab */}
          <TabsContent value="deliveries" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>Delivery History</CardTitle>
              </CardHeader>
              <CardContent>
                {deliveries.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No deliveries recorded</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-4 font-medium">Date</th>
                          <th className="text-left p-4 font-medium">Supplier</th>
                          <th className="text-left p-4 font-medium">Invoice</th>
                          <th className="text-right p-4 font-medium">Quantity</th>
                          <th className="text-right p-4 font-medium">Sub Total</th>
                          <th className="text-right p-4 font-medium">VAT</th>
                          <th className="text-right p-4 font-medium">Total</th>
                          <th className="text-center p-4 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deliveries.map((delivery) => {
                          const subTotal = delivery.sub_total || (delivery.total_cost / 100);
                          const vatAmount = delivery.vat_amount || (subTotal * 0.20);
                          const totalWithVat = delivery.total_with_vat || (subTotal * 1.20);
                          return (
                          <tr key={delivery.id} className="border-b hover:bg-muted/30">
                            <td className="p-4">{delivery.delivery_date}</td>
                            <td className="p-4">{delivery.supplier}</td>
                            <td className="p-4">{delivery.invoice_number || '-'}</td>
                            <td className="p-4 text-right font-mono">
                              {delivery.total_quantity?.toLocaleString()} L
                            </td>
                            <td className="p-4 text-right font-mono">
                              {formatCurrency(subTotal)}
                            </td>
                            <td className="p-4 text-right font-mono text-muted-foreground">
                              {formatCurrency(vatAmount)}
                            </td>
                            <td className="p-4 text-right font-mono font-semibold text-primary">
                              {formatCurrency(totalWithVat)}
                            </td>
                            <td className="p-4 text-center">
                              <div className="flex items-center justify-center gap-1">
                                {/* View button - available to all (managers included) */}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openViewDeliveryDialog(delivery)}
                                  data-testid={`view-delivery-${delivery.id}`}
                                  title="View delivery details"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                {/* Edit - Admin/Super User/Manager/Staff */}
                                {(isAdmin || isSuperUser || isManager || isStaff) && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openEditDeliveryDialog(delivery)}
                                    data-testid={`edit-delivery-${delivery.id}`}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                )}
                                {/* Delete - Admin/Super User only */}
                                {(isAdmin || isSuperUser) && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                    onClick={() => openDeleteDialog(delivery)}
                                    data-testid={`delete-delivery-${delivery.id}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Add/Edit Delivery Dialog - Compact Table Format */}
      <Dialog open={deliveryDialogOpen} onOpenChange={(open) => { setDeliveryDialogOpen(open); if (!open) setEditingDelivery(null); }}>
        <DialogContent className="max-w-xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              {editingDelivery ? 'Edit Fuel Delivery' : 'Add Fuel Delivery'}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-3 pr-2">
            {/* Header Info - Compact */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Delivery Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-xs h-9 px-2">
                      <CalendarIcon className="mr-1 h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{format(deliveryForm.delivery_date, 'dd/MM/yyyy')}</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={deliveryForm.delivery_date}
                      onSelect={(date) => date && setDeliveryForm({ ...deliveryForm, delivery_date: date })}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Invoice Number</Label>
                <Input
                  value={deliveryForm.invoice_number}
                  onChange={(e) => setDeliveryForm({ ...deliveryForm, invoice_number: e.target.value })}
                  className="h-9 text-sm"
                  data-testid="delivery-invoice"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Supplier</Label>
                <Select 
                  value={deliveryForm.supplier_id || deliveryForm.supplier || ''} 
                  onValueChange={(value) => {
                    const supplier = suppliers.find(s => s.id === value);
                    if (supplier) {
                      setDeliveryForm({ 
                        ...deliveryForm, 
                        supplier_id: value,
                        supplier: supplier.name
                      });
                    } else {
                      // User selected the typed value or empty
                      setDeliveryForm({ 
                        ...deliveryForm, 
                        supplier_id: '',
                        supplier: value
                      });
                    }
                  }}
                >
                  <SelectTrigger data-testid="delivery-supplier-select" className="h-9 text-sm">
                    <SelectValue placeholder="Select supplier" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map(supplier => (
                      <SelectItem key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Fuel Grades Table - Compact */}
            <div className="border border-primary/30 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-primary/20">
                  <tr>
                    <th className="text-left p-2 text-xs font-semibold text-primary">Grade</th>
                    <th className="text-center p-2 text-xs font-semibold text-primary">Quantity (L)</th>
                    <th className="text-center p-2 text-xs font-semibold text-primary">Cost (p/L)</th>
                    <th className="text-center p-2 text-xs font-semibold text-primary">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {deliveryForm.grades.map((grade, index) => {
                    const gradeColors = getFuelGradeColor(grade.grade_name);
                    return (
                    <tr key={grade.grade_id} className={`border-t border-border ${index % 2 === 0 ? 'bg-muted/20' : ''}`}>
                      <td className="p-2">
                        <div className="flex items-center gap-2">
                          <span className={`w-3 h-3 rounded-full ${gradeColors.bg}`}></span>
                          <span className="font-medium text-sm">{grade.grade_name}</span>
                        </div>
                      </td>
                      <td className="p-2 text-center">
                        <Input
                          type="number"
                          value={grade.quantity || ''}
                          onChange={(e) => updateGradeDelivery(grade.grade_id, 'quantity', e.target.value)}
                          className="w-24 mx-auto font-mono text-center text-sm h-8 border border-primary/50"
                          placeholder="0"
                          data-testid={`delivery-qty-${grade.grade_id}`}
                        />
                      </td>
                      <td className="p-2 text-center">
                        <Input
                          type="number"
                          step="0.001"
                          value={grade.cost_per_litre || ''}
                          onChange={(e) => updateGradeDelivery(grade.grade_id, 'cost_per_litre', e.target.value)}
                          className="w-24 mx-auto font-mono text-center text-sm h-8 border border-primary/50"
                          placeholder="0.000"
                          data-testid={`delivery-cost-${grade.grade_id}`}
                        />
                      </td>
                      <td className="p-2 text-center">
                        <span className={`font-mono font-semibold text-sm ${calculateGradeTotal(grade) > 0 ? 'text-green-500' : 'text-muted-foreground'}`}>
                          {calculateGradeTotal(grade) > 0 ? formatCurrency(calculateGradeTotal(grade)) : '—'}
                        </span>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-primary/10 border-t border-primary">
                  <tr>
                    <td colSpan="3" className="p-2 text-right font-medium text-sm text-muted-foreground">Sub Total:</td>
                    <td className="p-2 text-center">
                      <span className="font-mono font-semibold text-sm">
                        {formatCurrency(calculateInvoiceTotal())}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan="3" className="p-2 text-right font-medium text-sm text-muted-foreground">VAT (20%):</td>
                    <td className="p-2 text-center">
                      <span className="font-mono font-semibold text-sm">
                        {formatCurrency(calculateInvoiceTotal() * 0.20)}
                      </span>
                    </td>
                  </tr>
                  <tr className="bg-primary/20">
                    <td colSpan="3" className="p-2 text-right font-semibold text-sm text-primary">Invoice Total:</td>
                    <td className="p-2 text-center">
                      <span className="font-mono font-bold text-base text-primary">
                        {formatCurrency(calculateInvoiceTotal() * 1.20)}
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <DialogFooter className="pt-3 border-t border-border mt-3">
            <Button variant="outline" size="sm" onClick={() => { setDeliveryDialogOpen(false); setEditingDelivery(null); }}>Cancel</Button>
            <Button size="sm" onClick={handleDeliverySubmit} disabled={saving} data-testid="save-delivery-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingDelivery ? 'Update Delivery' : 'Save Delivery'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Price Dialog */}
      <Dialog open={priceDialogOpen} onOpenChange={setPriceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              Update Selling Price - {selectedGrade?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-4 bg-muted/50 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Current Average Cost</span>
                <span className="font-mono">{formatPennies(selectedGrade?.average_price, 3)}/L</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Current Selling Price</span>
                <span className="font-mono">{formatPennies(selectedGrade?.selling_price, 1)}/L</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>New Selling Price (p/L)</Label>
              <Input
                type="number"
                step="0.1"
                value={newSellingPrice}
                onChange={(e) => setNewSellingPrice(e.target.value)}
                className="font-mono"
                data-testid="new-selling-price"
              />
            </div>

            {newSellingPrice > 0 && selectedGrade && (
              <div className="p-3 bg-green-500/10 rounded-lg">
                <p className="text-sm text-muted-foreground">New Margin</p>
                <p className={`text-lg font-bold font-mono ${
                  (newSellingPrice - selectedGrade.average_price) >= 0 ? 'text-green-400' : 'text-red-400'
                }`}>
                  {formatPennies(newSellingPrice - selectedGrade.average_price, 1)}/L
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPriceDialogOpen(false)}>Cancel</Button>
            <Button onClick={handlePriceUpdate} disabled={saving} data-testid="update-price-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Update Price
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Delivery Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500" style={{ fontFamily: 'Barlow Condensed' }}>
              <AlertTriangle className="h-5 w-5" />
              Delete Fuel Delivery
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete this delivery? The fuel stock will be adjusted accordingly.
            </p>

            {deletingDelivery && (
              <div className="p-3 bg-muted/50 rounded-lg text-sm space-y-1">
                <p><strong>Date:</strong> {format(new Date(deletingDelivery.delivery_date), 'PPP')}</p>
                <p><strong>Supplier:</strong> {deletingDelivery.supplier}</p>
                <p><strong>Invoice:</strong> {deletingDelivery.invoice_number || 'N/A'}</p>
                <p><strong>Quantity:</strong> {deletingDelivery.total_quantity?.toLocaleString()} L</p>
                <p><strong>Total Cost:</strong> {formatCurrency(deletingDelivery.total_cost / 100)}</p>
              </div>
            )}

            <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <p className="text-sm text-yellow-600 dark:text-yellow-500">
                <strong>Warning:</strong> This will reduce the fuel stock by the delivery quantities and recalculate average prices.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteDelivery} 
              disabled={deleting}
              data-testid="confirm-delete-delivery-btn"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete Delivery
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Delivery Details Dialog - Read Only */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
              <Fuel className="h-5 w-5 text-primary" />
              Delivery Details
            </DialogTitle>
          </DialogHeader>

          {viewingDelivery && (
            <div className="space-y-4">
              {/* Delivery Info */}
              <div className="grid grid-cols-2 gap-3 p-3 bg-muted/50 rounded-lg text-sm">
                <div>
                  <span className="text-muted-foreground">Date:</span>
                  <p className="font-medium">{viewingDelivery.delivery_date}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Supplier:</span>
                  <p className="font-medium">{viewingDelivery.supplier}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Invoice #:</span>
                  <p className="font-medium">{viewingDelivery.invoice_number || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Total Quantity:</span>
                  <p className="font-medium font-mono">{viewingDelivery.total_quantity?.toLocaleString()} L</p>
                </div>
              </div>

              {/* Grade Details Table */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-primary/10 px-3 py-2 border-b">
                  <h4 className="font-medium text-sm">Fuel Grades</h4>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left p-2 font-medium">Grade</th>
                      <th className="text-right p-2 font-medium">Quantity (L)</th>
                      <th className="text-right p-2 font-medium">Cost/Litre</th>
                      <th className="text-right p-2 font-medium">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewingDelivery.grades?.map((grade, idx) => (
                      <tr key={idx} className="border-b last:border-0">
                        <td className="p-2 font-medium">{grade.grade_name}</td>
                        <td className="p-2 text-right font-mono">{grade.quantity?.toLocaleString()}</td>
                        <td className="p-2 text-right font-mono">{formatPennies(grade.cost_per_litre * 100, 2)}</td>
                        <td className="p-2 text-right font-mono">{formatCurrency(grade.quantity * grade.cost_per_litre)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-muted/50">
                    <tr className="border-t font-semibold">
                      <td className="p-2">Total</td>
                      <td className="p-2 text-right font-mono">{viewingDelivery.total_quantity?.toLocaleString()} L</td>
                      <td className="p-2"></td>
                      <td className="p-2 text-right font-mono text-primary">
                        {formatCurrency(viewingDelivery.sub_total || (viewingDelivery.total_cost / 100))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Cost Summary */}
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div className="p-2 bg-muted/30 rounded text-center">
                  <p className="text-muted-foreground text-xs">Sub Total</p>
                  <p className="font-mono font-medium">{formatCurrency(viewingDelivery.sub_total || (viewingDelivery.total_cost / 100))}</p>
                </div>
                <div className="p-2 bg-muted/30 rounded text-center">
                  <p className="text-muted-foreground text-xs">VAT (20%)</p>
                  <p className="font-mono font-medium">{formatCurrency(viewingDelivery.vat_amount || ((viewingDelivery.sub_total || (viewingDelivery.total_cost / 100)) * 0.20))}</p>
                </div>
                <div className="p-2 bg-primary/10 rounded text-center">
                  <p className="text-muted-foreground text-xs">Total with VAT</p>
                  <p className="font-mono font-semibold text-primary">{formatCurrency(viewingDelivery.total_with_vat || ((viewingDelivery.sub_total || (viewingDelivery.total_cost / 100)) * 1.20))}</p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FuelManagementPage;
